import React, { useState } from 'react';
import MapLocationPicker from '../locality/MapLocationPicker';
import StandardButton from '../core/StandardButton';
import AreaIdentityFields from '../shared/AreaIdentityFields';
import { useTranslation } from '../../i18n/useTranslation';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { AREA_COLORS } from '../../config/areaColors';
import { deriveAreaIdentityFromName, generateRandomAreaCode } from '../../utils/homeAreaSelfHealer';
import { DEFAULT_COUNTRY_CODE } from '../../data/countriesService';

/**
 * RegisterLocationStep — the location and area steps of new-account setup (AccountSetup).
 *
 * 2026-10-03 (Roadmap #88, registration confirm-first): the account now exists before these steps
 * run — the member has confirmed their email and is signed in — so the caller writes the result
 * straight away (createHomeAreaFromBoundary). The pre-account browser stash this component used to
 * feed is retired. Earlier history below.
 *
 * Two-stage, non-skippable location pick (Michael's ruling —
 * docs/briefs/2026-08-24-registration-wizard-and-deferred-home-area-build.md Part B):
 *   Stage 1 — single-pick the home parish.
 *   Stage 2 — build the surrounding área from the map, framed on the just-picked home with its
 *             surrounding parishes filling the view. This is the ONLY multiSelect surface the
 *             2026-08-26 crosshair-tuning brief's choreography/identity-box applies to
 *             (isHomeAreaBuilding) — MyLocations.jsx and AddEditGroup.jsx's own multi-select
 *             crosshair usage are untouched by that build; see the task report.
 *
 * This component itself writes nothing: `onComplete({ home, areaBoundaryIds, areaIdentity })` hands
 * the result to the caller (AccountSetup), which creates the home area. areaIdentity added
 * 2026-08-26 — see the identity state below.
 *
 * Reuses MapLocationPicker's existing opening-view resolution and UnifiedLocationSelector wiring
 * via its `embedded` (no modal chrome — this panel supplies its own) and `multiSelect` props,
 * rather than duplicating that ~170 lines of RPC-driven opening-view logic.
 */
const RegisterLocationStep = ({
  onComplete,
  onBack,
  // Defensive fallback only — LoginRegisterModal now always passes the resolved
  // existing-home/IP/configured-fallback chain (2026-09-02 country-selection foundation build).
  countryCode = DEFAULT_COUNTRY_CODE,
  initialHome = null,       // { boundaryId, name, countryCode, lat, lon } — resuming after Back from Step 3
  initialAreaIds = null,    // number[] — resuming after Back from Step 3
  initialIdentity = null    // { displayName, areaCode, colorHex } — resuming after Back from Step 3
}) => {
  const { t } = useTranslation('auth');
  const { isMobile } = useMediaQuery();
  const [stage, setStage] = useState(initialHome ? 'area' : 'home');
  const [home, setHome] = useState(initialHome);
  const [areaIds, setAreaIds] = useState(initialAreaIds && initialAreaIds.length > 0 ? initialAreaIds : (initialHome ? [initialHome.boundaryId] : []));
  // True only for a genuine Back-from-Step-3 resume, not a fresh stage-1-to-2 transition — the
  // `initialAreaIds` PROP is only ever populated by that resume path (handleHomeSelected always
  // pre-fills the `areaIds` STATE with just the home id regardless, so `areaIds.length > 0` alone
  // can't tell the two apart). Drives whether MapLocationPicker gets a pre-populated
  // initialBoundaries (resume — skip the enriched-opening re-seed) or none (fresh — let the
  // enriched opening's own get_boundaries_by_ids seed it, so handleTargetSeeded actually fires
  // and the identity box gets auto-named). Captured once; doesn't need to react to later change.
  const [isResuming] = useState(!!(initialAreaIds && initialAreaIds.length > 0));

  // Area identity (2026-08-26 crosshair tuning, item 5). Seeded from the target freguesia on
  // every (re-)seed via handleTargetSeeded; each field's auto value is overridden the instant
  // the user edits it and then sticks — a later re-seed only ever refreshes fields still in
  // "auto" state. This is wizard-local state only: nothing is persisted until Finish, so
  // "override wins" needs no schema here (a single fresh INSERT at the end just gets whatever
  // these values are by then). The *Custom flags are not sent downstream (see handleContinue) —
  // they exist only to arbitrate this component's own re-seed behaviour.
  // A resumed session (initialIdentity given) treats those values as already "custom": they carry
  // no per-field auto/custom provenance, so the safe default is to freeze them rather than risk
  // silently re-deriving over a value the user may have already settled on in an earlier pass.
  // (AccountSetup does not resume; the browser stash this once came from is retired, #88.)
  const [identity, setIdentity] = useState(() => ({
    displayName: initialIdentity?.displayName || '',
    areaCode: initialIdentity?.areaCode || '',
    colorHex: initialIdentity?.colorHex ?? null,
    displayNameCustom: !!initialIdentity,
    areaCodeCustom: !!initialIdentity,
    colorCustom: !!initialIdentity
  }));

  const handleHomeSelected = (selection) => {
    // selection.zoom (2026-08-26 save-and-exit/parity build, Part 3 P3) is the zoom the user had
    // chosen on this stage's map at the moment they confirmed — carried into the área stage's
    // preserveView below so that stage doesn't silently reframe to its own resolved bounding box.
    setHome(selection);
    setAreaIds([selection.boundaryId]);
    setStage('area');
  };

  const handleAreaChange = (ids) => {
    setAreaIds(ids || []);
  };

  const handleBackFromArea = () => {
    setStage('home');
  };

  // Fired by UnifiedLocationSelector (via MapLocationPicker) whenever a target is (re-)seeded —
  // the enriched opening on mount, or a later button click after a pan. Distinct from
  // handleAreaChange/onBoundaryChange, which also fires on ordinary grow/shrink clicks.
  const handleTargetSeeded = (target) => {
    // 2026-08-29 area-code collision fix, extended (docs/briefs/2026-08-29-area-code-collision-all-home-paths.md):
    // area_code is a random preview (generateRandomAreaCode) — deriveAreaIdentityFromName's own
    // areaCode is deterministic on name, the mechanism this fix replaces (still used for
    // displayName). This value (handed on by handleContinue below, created by AccountSetup) is
    // honoured only on createHomeAreaFromBoundary's FIRST insert attempt — a collision there
    // retries with a fresh random code rather than the same value forever (homeAreaSelfHealer.js's
    // own bounded retry loop), so no uniqueness check is needed here either.
    const { displayName } = deriveAreaIdentityFromName(target.name);
    setIdentity(prev => ({
      ...prev,
      displayName: prev.displayNameCustom ? prev.displayName : displayName,
      areaCode: prev.areaCodeCustom ? prev.areaCode : generateRandomAreaCode(),
      // Colour has no per-boundary derivation (there is no "as today" default to follow — the
      // home area's color_hex has never been auto-set before this build, confirmed against
      // createHomeAreaFromBoundary's pre-existing INSERT). AREA_COLORS[0] is a new default
      // introduced here so the identity box always shows a chosen swatch; flagged in the task
      // report, not silently presented as a continuation of existing behaviour.
      colorHex: prev.colorCustom ? prev.colorHex : (prev.colorHex ?? AREA_COLORS[0].hex)
    }));
  };

  const handleIdentityNameChange = (value) => {
    setIdentity(prev => ({ ...prev, displayName: value, displayNameCustom: true }));
  };

  const handleIdentityCodeChange = (value) => {
    setIdentity(prev => ({ ...prev, areaCode: value.toUpperCase(), areaCodeCustom: true }));
  };

  const handleIdentityColorChange = (hex) => {
    setIdentity(prev => ({ ...prev, colorHex: hex, colorCustom: true }));
  };

  const handleContinue = () => {
    // Home must always be present in the área, regardless of whether the user re-selected it
    // in the multi-select — the caller's createHomeAreaFromBoundary (AccountSetup) assumes it.
    const finalAreaIds = areaIds.includes(home.boundaryId)
      ? areaIds
      : [home.boundaryId, ...areaIds];
    // Pass the full `home` selection (not just its id) so the caller can resume this step
    // pre-filled if the user later clicks Back from Step 3. areaIdentity strips the *Custom
    // bookkeeping flags — createHomeAreaFromBoundary just wants the final values.
    onComplete({
      home,
      areaBoundaryIds: finalAreaIds,
      areaIdentity: {
        displayName: identity.displayName,
        areaCode: identity.areaCode,
        colorHex: identity.colorHex
      }
    });
  };

  const titleStyle = { margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', color: '#024B99', fontFamily: 'OpenSauceSans, sans-serif' };
  const promptStyle = { margin: '0 0 12px 0', fontSize: '13px', color: '#024B99', lineHeight: '1.4' };
  // Area-stage top zone (2026-08-26 crosshair tuning, item 4) — "+20%, bold, centred", computed
  // against this file's own existing sizes rather than a LAYOUT.md typography token (none exists
  // for this element — checked; see UnifiedLocationSelector.jsx's own instructionTextStyle
  // comment for the same finding). 13px x 1.2 = 15.6, rounded to 16. Stage 1 (titleStyle/
  // promptStyle above) is untouched — this brief scopes the rewrite to the área stage only.
  // Since #88 addendum 3 (A2) the área stage's prompt line is gone (the map's grow prompt carries
  // the one instruction), so only the centred title style remains here.
  const areaTitleStyle = { ...titleStyle, textAlign: 'center' };
  // 2026-08-26 map-spring/sizing fix: this frame was fixed at 340px regardless of viewport, but
  // what it wraps (UnifiedLocationSelector's own crosshair control row, ~50-70px depending on
  // state, plus its map div at mapHeight — 500px desktop / 350px mobile, per
  // UnifiedLocationSelector.jsx's own const — plus margins) is taller than that at every size,
  // and `overflow: hidden` was silently clipping the difference rather than erroring. `compact`
  // is passed to UnifiedLocationSelector but does not currently shrink mapHeight (confirmed: its
  // formula gives the same value whether compact is true or false) — not changed here, since
  // UnifiedLocationSelector is shared with MyLocations/AddEditGroup and this brief's fix is
  // scoped to this registration surface, not a shared sizing formula change. Sized generously
  // above the actual content instead of exactly to it, so a future control-row wording change
  // (e.g. a longer instruction string wrapping to two lines) doesn't reopen the same clip.
  // 2026-09-15 build-your-area stall fix (brief item 3). Was `height` + `overflow: hidden`, which
  // silently clipped anything the selector rendered past the frame — including its error banner
  // and loading line, which sit below a 500px (desktop) / 350px (mobile) map. A correctly-reported
  // failure was therefore invisible on this surface whatever its cause, which is the half of the
  // stall that made it unrecoverable rather than merely wrong. `minHeight` keeps the generous
  // floor the 2026-08-26 map-spring/sizing fix added (see that comment above — the frame must stay
  // taller than the control row + map + margins) while letting the panel grow instead of cutting.
  // The modal shell already supports this: LoginRegisterModal passes `alignTop`, so Modal.jsx
  // gives the panel overflowY:auto — a tall panel scrolls, it does not clip.
  const mapFrameStyle = { minHeight: isMobile ? '460px' : '620px', borderRadius: '12px', border: '1px solid #E5E7EB' };
  const navRowStyle = { display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '16px' };

  if (stage === 'home') {
    return (
      <div>
        <h3 style={titleStyle}>{t('wizard_location_home_title')}</h3>
        <p style={promptStyle}>{t('wizard_location_home_prompt')}</p>
        <div style={mapFrameStyle}>
          <MapLocationPicker
            // See the área stage's own key comment below — same reason, same
            // derivation. Both stages carry one so the pair reads as a single
            // decision rather than a one-sided patch.
            key="stage-home"
            mode="change"
            embedded
            multiSelect={false}
            onSelect={handleHomeSelected}
            countryCode={countryCode}
            pickerConfirmLabel={t('wizard_confirm_home_label')}
          />
        </div>
        {/* No Back on the first stage when there is nowhere to go back to (AccountSetup). */}
        {onBack && (
          <div style={navRowStyle}>
            <StandardButton variant="ghost" label={t('wizard_nav_back')} onClick={onBack} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* One title, one line (#88 addendum 3, A2): the instruction is the map's own grow prompt
          (growPromptLabel below), not a second line here. */}
      <h3 style={areaTitleStyle}>{t('wizard_location_area_title')}</h3>
      <div style={mapFrameStyle}>
        <MapLocationPicker
          // 2026-09-15 build-your-area stall fix (docs/briefs/2026-09-15-fix-build-your-area-stall.md,
          // item 1), diagnosed in that brief's probe RESULTS § 2. Without a key, React sees the
          // same component type at the same tree position across the stage-1 -> stage-2 flip and
          // REUSES the instance: MapLocationPicker is not remounted, and neither is the
          // UnifiedLocationSelector inside it (same type, same position in both of its branches).
          // Its enriched-opening seed effect (UnifiedLocationSelector.jsx:647-681) has a `[]`
          // dependency array, so it had already run during stage 1 — where isHomeAreaBuilding was
          // false and it early-returned — and never ran again once stage 2 turned that prop on.
          // Nothing seeded: no shaded polygon, no auto-filled Area Name, and therefore no
          // Save-and-exit button (gated on displayName below). preserveView is what hides the bug:
          // it makes MapLocationPicker's `resolving` false, so it never falls back to its
          // picker_loading div, which would otherwise have unmounted the selector and masked this.
          //
          // Keyed off `stage` alone, deliberately — NOT off home.boundaryId or areaIds, either of
          // which would remount mid-stage and re-run the seed effect underneath the user. `stage`
          // initialises to 'area' when initialHome is set (:44), so a Back-from-Step-3 resume
          // mounts straight into the área key and forces no extra remount there.
          //
          // Nothing is lost to the remount: everything stage 2 needs (countryCode, preserveView,
          // detectedBoundaryId, initialBoundaries) arrives as props, not as inherited internal
          // state. Precedent for keying this exact component: MyLocations.jsx:1410.
          key="stage-area"
          mode="change"
          embedded
          multiSelect
          initialBoundaries={isResuming ? areaIds : []}
          onBoundaryChange={handleAreaChange}
          detectedBoundaryId={home.boundaryId}
          countryCode={home.countryCode || countryCode}
          isHomeAreaBuilding
          onTargetSeeded={handleTargetSeeded}
          // #88 addendum 3: open framed on the home parish and all its neighbours, then stay where
          // the person puts it (A1); one instruction line (A2); the country was settled on the
          // previous stage, so no selector here — to change it, go Back (A3).
          frameFirstTierAdjacency
          growPromptLabel={t('wizard_location_area_prompt')}
          showCountrySelect={false}
          preserveView={home.lat != null && home.lon != null && home.zoom != null
            ? { center: [home.lat, home.lon], zoom: home.zoom }
            : null}
        />
      </div>

      {areaIds.length > 0 && (
        <AreaIdentityFields
          displayName={identity.displayName}
          areaCode={identity.areaCode}
          colorHex={identity.colorHex}
          onDisplayNameChange={handleIdentityNameChange}
          onAreaCodeChange={handleIdentityCodeChange}
          onColorHexChange={handleIdentityColorChange}
          // Addendum 1 (2026-08-26 save-and-exit/parity build, Part 3 P1): repointed to
          // MyLocations' own existing, live, correctly-rendering identity keys rather than the
          // parallel auth.wizard_identity_* family (which never existed on staging) — cross-
          // namespace via the 'user:' prefix, since this file's own default namespace is 'auth'.
          // Placeholders resolved per docs/escalations/2026-08-26-identity-placeholder-wording.md
          // (Option 2, ruled): this is a home-building surface, so it gets the home-flavoured
          // placeholder pair, not MyLocations' own arbitrary-area examples ("Weekend Beach
          // Spots"/"BEACH1") — labels stay the shared set above.
          nameLabel={t('user:locations_form_area_name_label')}
          namePlaceholder={t('user:locations_form_area_name_placeholder_home')}
          codeLabel={t('user:locations_form_area_code_label')}
          codePlaceholder={t('user:locations_form_area_code_placeholder_home')}
          colorLabel={t('user:locations_form_area_color_label')}
        />
      )}

      {/* Save and exit — this stage's one "finish" action, below the map so the person sees the map
          first (#88 addendum 3, A4; it used to sit in the map's control zone above it). Wording
          unchanged (location:save_and_exit).
          2026-08-29 addendum (docs/briefs/2026-08-29-addendum-registration-home-path-vs-is-home-index.md,
          Advisor STRONGLY ADVISE), kept: gated on a non-blank displayName as well as a 6-character
          areaCode, so a blank home name can never be submitted (create_home_area_if_none_exists
          refuses it, migration 20260829b_home_area_atomic_create_rpc.sql). */}
      <div style={navRowStyle}>
        <StandardButton variant="ghost" label={t('wizard_nav_back')} onClick={handleBackFromArea} />
        {areaIds.length > 0 && identity.areaCode.length === 6 && identity.displayName.trim() && (
          <StandardButton variant="action" label={t('location:save_and_exit')} onClick={handleContinue} />
        )}
      </div>
    </div>
  );
};

export default RegisterLocationStep;
